import { useRef } from 'react';
import type { ReactNode, Ref } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import DialogActions from '@mui/material/DialogActions';
import Tooltip from '@mui/material/Tooltip';
import type { SxProps, Theme } from '@mui/material/styles';
import { moveAmongButtons } from '../../utils/dialogButtonKeys';
import { useDialogKeys } from '../../hooks/useDialogKeys';
import { useDialogAutoFocus } from '../../hooks/useDialogAutoFocus';
import { SECONDARY_BUTTON_SX } from './dialogActionsSx';

/* ==========================================================================
   Dialog and form buttons, laid out by Jeff's rule (docs/ui/dialog-actions.md):

     [Delete] [leftSlot]             [extra...] [Cancel] [Save]
      red, far left                  right-aligned; Save is the only filled
                                     button and sits at the far right

   Enter = Save, Esc = Cancel. Tab order follows what you see:
   Delete, extra, Cancel, Save. In a dialog that saves, the first field has
   the focus when it opens (useDialogAutoFocus).
   ========================================================================== */

export interface DialogActionsBarProps {
  /** Cancel / Close. Also what Esc does outside an MUI Dialog (inside one, the Dialog's own onClose handles Esc). */
  onCancel: () => void;
  /** Save. Leave out for a dialog that only views (then there is no Save button and Enter does nothing). */
  onSave?: () => void;
  /** Default "Save". */
  saveLabel?: string;
  /** Shown on Save while `saving`. Default "Saving...". */
  savingLabel?: string;
  /** Save is greyed out and Enter does nothing (form invalid, nothing changed, no permission). */
  saveDisabled?: boolean;
  /** A save is in flight: Save shows progress, and Save, Delete, Cancel, Enter and Esc all wait for it. */
  saving?: boolean;
  /** Delete. When given, a red Delete button sits at the far left, apart from the others. */
  onDelete?: () => void;
  /** Default "Delete". The visible text is the button's accessible name. */
  deleteLabel?: string;
  deleteDisabled?: boolean;
  /** Other secondary buttons (Print, Copy, Reset...): placed between Delete and Cancel. Use outlined MUI Buttons. */
  extra?: ReactNode;
  /** Save's colour. Default 'primary'; 'error' / 'warning' for a Save that removes or overwrites. */
  saveColor?: 'primary' | 'error' | 'warning';
  /** An icon before the Save label (replaced by the progress spinner while saving). */
  saveIcon?: ReactNode;
  /** Tooltip on Save, shown even while Save is disabled (say why it is greyed out). */
  saveTooltip?: ReactNode;
  /** Tooltip on Delete, shown even while Delete is disabled (e.g. "Your role cannot delete these"). */
  deleteTooltip?: ReactNode;
  /** A control or hint at the left, after Delete: an Inactive checkbox, a keys hint, "Unsaved changes". */
  leftSlot?: ReactNode;
  /** Default "Cancel". Use "Close" when nothing on the dialog can be edited. */
  cancelLabel?: string;
  /** Wire Enter = Save and Esc = Cancel for the dialog (or form) this bar sits in. Default true. */
  keyboard?: boolean;
  /**
   * Enter = Save. Default true. False keeps Esc = Cancel but Enter never presses Save
   * (a destructive prompt: Enter on a focused button is still that button's click).
   */
  enterSaves?: boolean;
  /** The button that takes the focus when the bar mounts (MUI `autoFocus`). Default: neither. */
  initialFocus?: 'save' | 'cancel';
  /**
   * In a dialog that can save (`onSave` or `primary`), put the focus in the dialog's first
   * field when it opens, caret at the end of any text (see useDialogAutoFocus). Default true.
   * Off when `initialFocus` names a button. Set false for a dialog that places the focus
   * itself; mark a single picker that opens on focus with `data-no-autofocus` instead.
   */
  autoFocusField?: boolean;
  /** Refs to the Cancel and Save buttons, for a dialog that moves the focus itself (e.g. once its transition ends). */
  cancelRef?: Ref<HTMLButtonElement>;
  saveRef?: Ref<HTMLButtonElement>;
  /**
   * A custom primary in place of the Save button, still at the far right (a split button such as
   * "Save and Open" with a chevron menu). It must be the only filled control. Enter calls `onSave`
   * when that is given too; leave `onSave` out and Enter does nothing.
   */
  primary?: ReactNode;
  className?: string;
  /** Passed to MUI DialogActions (padding, borders); the button order is not configurable. */
  sx?: SxProps<Theme>;
}

const DELETE_SX: SxProps<Theme> = {
  color: 'var(--color-negative)',
  borderColor: 'var(--color-negative)',
  bgcolor: 'var(--card-bg)',
  '&:hover': { borderColor: 'var(--color-negative)', bgcolor: 'var(--color-negative-bg)' },
};

/** A tooltip that still shows on a disabled button (MUI needs a wrapper that gets the mouse events). */
function withTooltip(title: ReactNode, button: ReactNode): ReactNode {
  if (title == null || title === false || title === '') return button;
  return (
    <Tooltip title={title}>
      <span style={{ display: 'inline-flex' }}>{button}</span>
    </Tooltip>
  );
}

export default function DialogActionsBar({
  onCancel,
  onSave,
  saveLabel = 'Save',
  savingLabel = 'Saving...',
  saveDisabled = false,
  saving = false,
  onDelete,
  deleteLabel = 'Delete',
  deleteDisabled = false,
  extra,
  saveColor = 'primary',
  saveIcon,
  saveTooltip,
  deleteTooltip,
  leftSlot,
  cancelLabel = 'Cancel',
  keyboard = true,
  enterSaves = true,
  initialFocus,
  autoFocusField = true,
  cancelRef,
  saveRef,
  primary,
  className,
  sx,
}: DialogActionsBarProps) {
  const barRef = useRef<HTMLDivElement | null>(null);

  useDialogKeys(barRef, {
    onSave: enterSaves ? onSave : undefined,
    onCancel,
    saveDisabled,
    saving,
    enabled: keyboard,
    scope: 'nearest',
  });

  useDialogAutoFocus(barRef, autoFocusField && !initialFocus && (onSave != null || primary != null));

  return (
    <DialogActions
      ref={barRef}
      disableSpacing
      className={['dialog-actions-bar', className].filter(Boolean).join(' ')}
      onKeyDown={moveAmongButtons}
      sx={[{ gap: 1, flexWrap: 'wrap', justifyContent: 'flex-start' }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {onDelete && withTooltip(deleteTooltip, (
        <Button
          variant="outlined"
          color="error"
          onClick={onDelete}
          disabled={deleteDisabled || saving}
          sx={DELETE_SX}
          data-testid="dialog-actions-delete"
        >
          {deleteLabel}
        </Button>
      ))}
      {leftSlot != null && leftSlot !== false && (
        <Box className="dialog-actions-bar-left" sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {leftSlot}
        </Box>
      )}
      {/* One group, so a narrow dialog wraps Delete onto its own line and never splits Cancel from Save. */}
      <Box className="dialog-actions-bar-main" sx={{ display: 'flex', gap: 1, ml: 'auto', alignItems: 'center' }}>
        {extra}
        <Button
          variant="outlined"
          ref={cancelRef}
          onClick={onCancel}
          disabled={saving}
          autoFocus={initialFocus === 'cancel'}
          sx={SECONDARY_BUTTON_SX}
          data-testid="dialog-actions-cancel"
        >
          {cancelLabel}
        </Button>
        {primary}
        {primary == null && onSave && withTooltip(saveTooltip, (
          <Button
            variant="contained"
            color={saveColor}
            ref={saveRef}
            onClick={onSave}
            autoFocus={initialFocus === 'save'}
            disabled={saveDisabled || saving}
            aria-busy={saving || undefined}
            startIcon={saving ? <CircularProgress size={14} color="inherit" aria-hidden /> : saveIcon}
            data-testid="dialog-actions-save"
          >
            {saving ? savingLabel : saveLabel}
          </Button>
        ))}
      </Box>
    </DialogActions>
  );
}
